import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowUpRight, CalendarClock, Loader2, Phone, PhoneMissed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { WhatsAppCallbackButton } from "@/components/common/WhatsAppCallbackButton";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";

const GROUPS = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tone: "text-red-600", badge: "bg-red-50 text-red-700 border-red-200" },
  { key: "today", label: "Today", icon: PhoneMissed, tone: "text-amber-600", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "upcoming", label: "Upcoming", icon: CalendarClock, tone: "text-primary", badge: "bg-primary/10 text-primary border-primary/20" },
];

export default function RecruiterCallbacks() {
  const [data, setData] = useState({ items: [], today: "" });
  const [loading, setLoading] = useState(true);
  const [reschedOf, setReschedOf] = useState(null);
  const [reschedDate, setReschedDate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/recruiter-flow/callbacks")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = { overdue: [], today: [], upcoming: [] };
    const today = data.today;
    for (const it of data.items) {
      if (!it.callback_date || it.callback_date < today) g.overdue.push(it);
      else if (it.callback_date === today) g.today.push(it);
      else g.upcoming.push(it);
    }
    return g;
  }, [data]);

  const submitReschedule = async () => {
    if (!reschedDate) return toast.error("Pick a new call back date");
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/applications/${reschedOf.application_id}/callback`, { followup_date: reschedDate });
      toast.success(`Call back moved to ${formatDate(reschedDate)}`);
      setReschedOf(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const totalCount = data.items.length;

  return (
    <div className="space-y-6" data-testid="callbacks-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
          <PhoneMissed className="h-6 w-6 text-amber-600" /> Call Backs
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Candidates you couldn't reach — call them back on the date you set. They stay with you and don't count against your working slot.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
      ) : totalCount === 0 ? (
        <EmptyState icon={Phone} title="No call backs pending"
          description="When you mark a candidate as 'No Answer / Call Back', they'll show up here grouped by date." />
      ) : (
        <div className="space-y-8">
          {GROUPS.map(({ key, label, icon: Icon, tone, badge }) => {
            const items = grouped[key];
            if (items.length === 0) return null;
            return (
              <div key={key} className="space-y-3" data-testid={`callback-group-${key}`}>
                <h2 className={`text-sm font-heading font-semibold flex items-center gap-1.5 ${tone}`}>
                  <Icon className="h-4 w-4" /> {label}
                  <span className="text-muted-foreground font-normal">({items.length})</span>
                </h2>
                <div className="space-y-2.5">
                  {items.map((it) => (
                    <Card key={it.application_id} className="rounded-xl border-border shadow-none" data-testid="callback-item">
                      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link to={`/recruiter/applications/${it.application_id}`}
                              className="font-medium hover:underline" data-testid="callback-candidate-link">
                              {it.candidate_name}
                            </Link>
                            {it.candidate_code && <span className="font-mono text-xs text-muted-foreground">{it.candidate_code}</span>}
                            <Badge variant="outline" className={`rounded-full text-xs ${badge}`} data-testid="callback-date-badge">
                              {formatDate(it.callback_date)}
                            </Badge>
                            {it.callback_count > 1 && (
                              <Badge variant="outline" className="rounded-full text-xs bg-slate-100 text-slate-600 border-slate-200" data-testid="callback-attempts-badge">
                                Attempt #{it.callback_count}
                              </Badge>
                            )}
                          </div>
                          {it.candidate_mobile && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                              <Phone className="h-3.5 w-3.5" /> {it.candidate_mobile}
                            </p>
                          )}
                          {it.callback_note && <p className="text-xs text-muted-foreground italic">"{it.callback_note}"</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                          <Button variant="outline" size="sm" className="rounded-lg bg-white"
                            onClick={() => { setReschedOf(it); setReschedDate(it.callback_date || ""); }}
                            data-testid={`callback-reschedule-${it.application_id}`}>
                            <CalendarClock className="h-4 w-4 mr-1.5" /> Reschedule
                          </Button>
                          <WhatsAppCallbackButton candidateName={it.candidate_name} candidateMobile={it.candidate_mobile} />
                          <Button asChild size="sm" className="rounded-lg" data-testid={`callback-open-${it.application_id}`}>
                            <Link to={`/recruiter/applications/${it.application_id}`}>
                              Open to act <ArrowUpRight className="h-4 w-4 ml-1" />
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!reschedOf} onOpenChange={(o) => !o && setReschedOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="callback-reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reschedule call back</DialogTitle>
            <DialogDescription>
              Pick a new date to try {reschedOf?.candidate_name} again. To close the lead, open the candidate and choose a final action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>New call back date</Label>
              <Input type="date" data-testid="callback-reschedule-date-input" value={reschedDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setReschedDate(e.target.value)} />
            </div>
            <Button onClick={submitReschedule} disabled={busy} className="w-full rounded-lg" data-testid="callback-reschedule-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update call back date
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
